import { FC, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Icon } from 'src/components';
import PlaceholderIllustration from './assets/placeholder.svg';
import { GoodLuckCelebrationMedia } from './useGoodLuckCelebrationMedia';
import * as S from './styles';

const msgs = defineMessages({
  badge: {
    id: 'path_pre_checkpoint_badge',
    defaultMessage: 'Section complete',
  },
  title: {
    id: 'path_pre_checkpoint_title',
    defaultMessage: 'Look how far you’ve come',
  },
  description: {
    id: 'path_pre_checkpoint_description',
    defaultMessage:
      'You’ve finished this section. Next, a quick checkpoint to lock it in and move your path forward.',
  },
  continueLabel: {
    id: 'path_pre_checkpoint_continue',
    defaultMessage: 'Continue to checkpoint',
  },
  backLabel: {
    id: 'path_pre_checkpoint_back',
    defaultMessage: 'Back to my path',
  },
  playVideo: {
    id: 'path_pre_checkpoint_play_video',
    defaultMessage: 'Play video',
  },
});

export type PreCheckpointScreenViewProps = {
  media: GoodLuckCelebrationMedia;
  onContinue: () => void;
  isContinuing?: boolean;
  onBack: () => void;
};

export const PreCheckpointScreenView: FC<PreCheckpointScreenViewProps> = ({
  media,
  onContinue,
  isContinuing = false,
  onBack,
}) => {
  const intl = useIntl();
  const videoSrc = media.status === 'video' ? media.videoSrc : undefined;
  const videoLabel = media.status === 'video' ? media.phrase : undefined;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPlayButton, setShowPlayButton] = useState(false);

  useEffect(() => {
    setShowPlayButton(false);

    if (!videoSrc) {
      return;
    }

    const video = videoRef.current;
    /* istanbul ignore next: the video is mounted whenever videoSrc is set */
    if (!video) {
      return;
    }

    video.muted = false;
    const playback = video.play();
    if (playback && typeof playback.catch === 'function') {
      void playback.catch(() => setShowPlayButton(true));
    }
  }, [videoSrc]);

  const replayVideo = () => {
    const video = videoRef.current;
    /* istanbul ignore next: the play control is only rendered with the video */
    if (!video) {
      return;
    }
    video.currentTime = 0;
    void video.play();
  };

  return (
    <S.Overlay data-testid="pre-checkpoint-screen">
      {media.status === 'audio' && (
        // Decorative pronunciation clip; captions are not applicable.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio autoPlay preload="auto" src={media.audioSrc} data-testid="pre-checkpoint-audio" />
      )}

      <S.Content>
        <S.Label>{intl.formatMessage(msgs.badge)}</S.Label>
        <S.Title>{intl.formatMessage(msgs.title)}</S.Title>
        <S.Description>{intl.formatMessage(msgs.description)}</S.Description>

        <S.Stage>
          {videoSrc ? (
            <>
              <S.Video
                ref={videoRef}
                src={videoSrc}
                playsInline
                preload="auto"
                aria-label={videoLabel}
                onPlay={() => setShowPlayButton(false)}
                onEnded={() => setShowPlayButton(true)}
                onError={() => setShowPlayButton(true)}
                data-testid="pre-checkpoint-video"
              >
                <track kind="captions" />
              </S.Video>
              {showPlayButton && (
                <S.PlayButton
                  type="button"
                  onClick={replayVideo}
                  aria-label={intl.formatMessage(msgs.playVideo)}
                  data-testid="pre-checkpoint-play"
                >
                  <Icon name="playFilled" />
                </S.PlayButton>
              )}
            </>
          ) : (
            <S.Placeholder data-testid="pre-checkpoint-placeholder">
              <PlaceholderIllustration aria-hidden="true" />
            </S.Placeholder>
          )}
        </S.Stage>

        {(media.status === 'video' || media.status === 'audio') && (
          <S.Caption data-testid="pre-checkpoint-caption">
            <S.Phrase dir="auto">{media.phrase}</S.Phrase>
            <S.Translation>{media.translation}</S.Translation>
          </S.Caption>
        )}

        <S.Footer>
          <Button
            buttonType="primary"
            buttonSize="large"
            wide
            onClick={onContinue}
            loading={isContinuing}
            disabled={isContinuing}
            data-testid="pre-checkpoint-continue-button"
          >
            {intl.formatMessage(msgs.continueLabel)}
          </Button>
          <Button
            buttonType="secondary"
            buttonSize="large"
            wide
            onClick={onBack}
            disabled={isContinuing}
            data-testid="pre-checkpoint-back-button"
          >
            {intl.formatMessage(msgs.backLabel)}
          </Button>
        </S.Footer>
      </S.Content>
    </S.Overlay>
  );
};
